<template>
  <div class="graph">
    <svg width="100%" height="262" viewBox="0 0 520 262" fill="none">
      <path v-for="(current, index) in angleData" :key="`pie-chart-${id}-${index}-path`" :d="getPathD(...current)" :fill="colors[index]" />
      <text v-for="(current, index) in angleData" :key="`pie-chart-${id}-${index}-text`" :x="getTextX(...current)" :y="getTextY(...current)" class="pie-text" text-anchor="middle">
        {{getText(...current)}}
      </text>
      <text v-for="(current, index) in legendData" :key="`pie-chart-${id}-${index}-legend-text`" :y="current.textY" x="310" class="legend">
        {{$t(current.text)}}
      </text>
      <rect v-for="(current, index) in legendData" :key="`pie-chart-${id}-${index}-legend-rect`" :y="current.rectY" x="286" width="16" height="7.75" :fill="colors[index]" />
    </svg>
  </div>
</template>

<style lang="scss" scoped>

.pie-text {
  font-size: var(--text--s);
  fill: var(--color--white);
}

.legend {
  font-size: var(--text--xs);
  fill: var(--color--secondary);
}

</style>

<script>
import { v4 as uuid } from 'uuid'

import vec2 from '@/helpers/vec2'
import angle from '@/helpers/angle'

const PIE_CHART_TEXT_RADIUS = 95
const PIE_CHART_X = 131
const PIE_CHART_Y = 131
const PIE_CHART_OUTER_RADIUS = 131
const PIE_CHART_INNER_RADIUS = 66
const PIE_CHART_HEIGHT = 262
const PIE_CHART_HALF_HEIGHT = PIE_CHART_HEIGHT * 0.5
const PIE_CHART_LEGEND_HEIGHT = 32

export default {
  props: {
    id: {
      type: String,
      default: uuid()
    },
    data: {
      type: Array,
      default: () => []
    },
    colors: {
      type: Array,
      default: () => ['#248F79', '#EBAF47', '#CC9433', '#C45F4F', '#86392D']
    },
    legends: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    percentualData () {
      const sum = this.data.reduce((acc, value) => acc + value, 0)
      return this.data.map((value) => value / sum)
    },
    angleData () {
      return this.percentualData.map((value, index, list) => {
        return [
          value * 360,
          list.reduce((acc, value, jndex) => acc + (jndex < index ? value : 0), 0) * 360
        ]
      })
    },
    legendData () {
      return this.legends.map((text, index, list) => {
        const textStartY = PIE_CHART_HALF_HEIGHT - Math.floor(list.length * 0.5) * PIE_CHART_LEGEND_HEIGHT
        const rectStartY = textStartY - 8
        const textY = textStartY + index * PIE_CHART_LEGEND_HEIGHT
        const rectY = rectStartY + index * PIE_CHART_LEGEND_HEIGHT
        return { text, textY, rectY }
      })
    }
  },
  methods: {
    getPathD (arcDegrees, startDegrees = 0) {
      const r = PIE_CHART_OUTER_RADIUS
      const ir = PIE_CHART_INNER_RADIUS
      const centerX = PIE_CHART_X
      const centerY = PIE_CHART_Y
      const startRadians = angle.degreesToRadians(startDegrees)
      const arcRadians = angle.degreesToRadians(arcDegrees)
      const [ax, ay] = vec2.fromPolar(startRadians, ir, centerX, centerY)
      const [bx, by] = vec2.fromPolar(startRadians, r, centerX, centerY)
      const [cx, cy] = vec2.fromPolar(startRadians + arcRadians, r, centerX, centerY)
      const [dx, dy] = vec2.fromPolar(startRadians + arcRadians, ir, centerX, centerY)
      const largeArc = arcDegrees > 180 ? 1 : 0
      return `
        M${ax},${ay}
        L${bx},${by}
        A${r},${r},${arcDegrees},${largeArc},1,${cx},${cy}
        L${dx},${dy}
        A${ir},${ir},${arcDegrees},${largeArc},0,${ax},${ay}
        Z
      `
    },
    getTextX (arcDegrees, startDegrees = 0) {
      const tr = PIE_CHART_TEXT_RADIUS
      const centerX = PIE_CHART_X
      const centerY = PIE_CHART_Y
      const startRadians = angle.degreesToRadians(startDegrees)
      const arcRadians = angle.degreesToRadians(arcDegrees)
      return vec2.fromPolarX(startRadians + arcRadians * 0.5, tr, centerX, centerY)
    },
    getTextY (arcDegrees, startDegrees = 0) {
      const tr = PIE_CHART_TEXT_RADIUS
      const centerX = PIE_CHART_X
      const centerY = PIE_CHART_Y
      const startRadians = angle.degreesToRadians(startDegrees)
      const arcRadians = angle.degreesToRadians(arcDegrees)
      return vec2.fromPolarY(startRadians + arcRadians * 0.5, tr, centerX, centerY)
    },
    getText (arcDegrees, startDegrees = 0) {
      return `${(arcDegrees * 100 / 360).toFixed(1)}%`
    }
  }
}
</script>
